import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logOut } from "../../API/API";
import {
  setActiveStep,
  setFiles,
  setFormData,
  setUser,
} from "../../ReduxStore/UserSlice";
import { TOKEN_KEYS } from "./Util/Util";

const LOGOUT_EVENT_KEY = "dequitec_logout_event";
const LOGOUT_TIMESTAMP_KEY = "dequitec_logout_timestamp";
const DASHBOARD_CLOSE_FLAG = "dequitec_dashboard_close";
const TEMP_LOGOUT_TOKEN = "temp_logout_token";

const LogoutDialog = ({ open, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [logoutBlocked, setLogoutBlocked] = useState(false);

  React.useEffect(() => {
    const handleLogoutEvent = (event) => {
      if (event.key === LOGOUT_EVENT_KEY) {
        performLogoutCleanup();
      }
    };

    window.addEventListener("storage", handleLogoutEvent);
    return () => {
      window.removeEventListener("storage", handleLogoutEvent);
    };
  }, []);

  React.useEffect(() => {
    const handleDraftSaved = (event) => {
      const { detail } = event;
      if (detail && detail.proceedWithLogout && logoutBlocked) {
        setLogoutBlocked(false);
        performLogoutAndCleanup();
      }
    };

    window.addEventListener("draftSaved", handleDraftSaved);

    return () => {
      window.removeEventListener("draftSaved", handleDraftSaved);
    };
  }, [logoutBlocked]);

  const performLogoutCleanup = () => {
    localStorage.setItem("manualLogoutConfirmed", "true");

    const token = localStorage.getItem(TOKEN_KEYS.JWT);
    if (localStorage.getItem("manualLogoutConfirmed") === "true") {
      try {
        if (token) {
          localStorage.setItem(TEMP_LOGOUT_TOKEN, token);
        }

        localStorage.removeItem(LOGOUT_TIMESTAMP_KEY);
        localStorage.removeItem(DASHBOARD_CLOSE_FLAG);
        localStorage.removeItem(LOGOUT_EVENT_KEY);
        sessionStorage.removeItem("registrationData");
        localStorage.removeItem("sessionId");
        dispatch(setUser({}));
        dispatch(setFormData({}));
        dispatch(setFiles({}));
        dispatch(setActiveStep(0));

        localStorage.removeItem(TEMP_LOGOUT_TOKEN);
        localStorage.removeItem(TOKEN_KEYS.BUSINESS_TYPE);
        localStorage.removeItem(TOKEN_KEYS.ENTITY_TYPE);
        localStorage.removeItem(TOKEN_KEYS.JWT);
        localStorage.removeItem(TOKEN_KEYS.USER_INFO);
        localStorage.removeItem("manualLogoutConfirmed");
        localStorage.removeItem("lenderId");
        localStorage.removeItem("userType");
        localStorage.removeItem("user");
        localStorage.removeItem("jwtToken");
        localStorage.removeItem("tempJwtToken");
        localStorage.removeItem("tempUuid");
        localStorage.removeItem("lenderRegistrationData");
        localStorage.removeItem(TOKEN_KEYS.USER_TYPE);

        // Navigate safely
        navigate("/");
      } catch (error) {
        localStorage.removeItem("manualLogoutConfirmed");
        navigate("/");
      }
    } else {
      console.warn(
        "[LOGOUT] Attempted logout cleanup without confirmation flag"
      );
      navigate("/");
    }
  };

  const performLogoutAndCleanup = () => {
    const token = localStorage.getItem(TOKEN_KEYS.JWT);
    if (token) {
      localStorage.setItem(TEMP_LOGOUT_TOKEN, token);
    }

    if (!localStorage.getItem(TOKEN_KEYS.JWT) && token) {
      localStorage.setItem(TOKEN_KEYS.JWT, token);
    }

    logOut()
      .then(() => {})
      .catch((error) => {
        console.error("Logout API call failed:", error);
      })
      .finally(() => {
        localStorage.setItem(LOGOUT_TIMESTAMP_KEY, Date.now().toString());
        localStorage.setItem(DASHBOARD_CLOSE_FLAG, "true");
        localStorage.setItem(LOGOUT_EVENT_KEY, Date.now().toString());

        if (
          !localStorage.getItem(TOKEN_KEYS.JWT) &&
          localStorage.getItem(TEMP_LOGOUT_TOKEN)
        ) {
          localStorage.setItem(
            TOKEN_KEYS.JWT,
            localStorage.getItem(TEMP_LOGOUT_TOKEN)
          );
        }

        performLogoutCleanup();
        onClose();
      });
  };

  const handleLogoutConfirm = () => {
    // Check if we're currently on the Apply Loan page
    const isOnApplyLoan = window.location.pathname === "/apply-loan";

    if (isOnApplyLoan) {
      // Create a custom event to notify ApplyLoan component about the logout
      const event = new CustomEvent("beforeLogout", {
        bubbles: true,
        cancelable: true,
      });

      // Dispatch the event
      const eventResult = window.dispatchEvent(event);

      // If the event was prevented, don't proceed with logout
      // The ApplyLoan component will handle it after showing the draft dialog
      if (!eventResult) {
        setLogoutBlocked(true);
        return;
      }
    }

    // If not blocked, proceed with logout
    performLogoutAndCleanup();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "400px",
          borderRadius: "16px",
          padding: "24px",
          textAlign: "center",
        },
      }}
    >
      <Box sx={{ mb: 3 }}>
        <img
          src="/Logout.png"
          alt="Logout"
          style={{ width: "64px", height: "64px" }}
        />
      </Box>

      <DialogTitle
        sx={{
          color: "#0046B5",
          fontSize: "20px",
          fontWeight: 600,
          padding: "0 24px 8px",
        }}
      >
        Are you sure you want to log out?
      </DialogTitle>

      <DialogContent>
        <Typography
          sx={{
            color: "#666666",
            fontSize: "16px",
            fontStyle: "italic",
          }}
        >
          You will be logged out from all open sessions.
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "center",
          gap: 2,
          padding: "24px",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            width: "160px",
            height: "48px",
            borderRadius: "8px",
            borderColor: "#0046B5",
            color: "#0046B5",
            textTransform: "none",
            fontSize: "16px",
            fontWeight: 600,
            "&:hover": {
              borderColor: "#003B82",
              backgroundColor: "rgba(0, 70, 181, 0.04)",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleLogoutConfirm}
          variant="contained"
          sx={{
            width: "160px",
            height: "48px",
            borderRadius: "8px",
            backgroundColor: "#0046B5",
            color: "white",
            textTransform: "none",
            fontSize: "16px",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "#003B82",
            },
          }}
        >
          Logout
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LogoutDialog;
